import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { sanitizeTextForWinAnsiPdf } from "@/utils/pdfWinAnsiText";

const PAGE_MARGIN = 50;
const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
const CONTENT_WIDTH = PAGE_WIDTH - 2 * PAGE_MARGIN;

const PRIMARY_COLOR = rgb(0.145, 0.388, 0.922);
const GRAY_TEXT = rgb(0.42, 0.45, 0.51);
const BLACK_TEXT = rgb(0.11, 0.11, 0.11);
const LIGHT_GRAY = rgb(0.96, 0.97, 0.98);

const ORDERED_KEYS = [
  "companyName",
  "registrationNumber",
  "companyType",
  "companyStatus",
  "dateOfIncorporation",
  "companyAddress",
  "natureOfBusiness",
  "authorizedCapital",
  "paidUpCapital",
  "email",
  "phoneNumber",
] as const;

const LABEL_OVERRIDES: Partial<Record<(typeof ORDERED_KEYS)[number], string>> = {
  companyName: "COMPANY NAME",
  dateOfIncorporation: "REGISTRATION DATE OF CERTIFICATE",
};

function formatLabelFromKey(key: string): string {
  return key.replace(/([A-Z])/g, " $1").trim().toUpperCase();
}

type Row = { label: string; value: string };

function collectRows(data: Record<string, unknown>): Row[] {
  const skip = new Set(["_verificationSource"]);
  const orderedSet = new Set<string>(ORDERED_KEYS);
  const rows: Row[] = [];

  for (const key of ORDERED_KEYS) {
    if (skip.has(key)) continue;
    const v = data[key];
    if (v === undefined || v === null || v === "") continue;
    if (typeof v === "object") continue;
    rows.push({
      label: LABEL_OVERRIDES[key] ?? formatLabelFromKey(key),
      value: String(v),
    });
  }

  for (const key of Object.keys(data)) {
    if (skip.has(key) || orderedSet.has(key)) continue;
    const v = data[key];
    if (v === undefined || v === null || v === "") continue;
    if (typeof v === "object") continue;
    rows.push({ label: formatLabelFromKey(key), value: String(v) });
  }

  return rows;
}

export async function downloadSystemVerificationPdf(verificationData: Record<string, unknown>): Promise<void> {
  const source = verificationData._verificationSource;
  const generatedAt = new Date();
  const rows = collectRows(verificationData);

  const pdfDoc = await PDFDocument.create();
  const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  let page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  let y = PAGE_HEIGHT - PAGE_MARGIN;

  const drawText = (
    text: string,
    x: number,
    startY: number,
    options: {
      size?: number;
      font?: typeof helvetica;
      color?: typeof BLACK_TEXT;
      maxWidth?: number;
      lineHeight?: number;
    } = {}
  ): number => {
    const {
      size = 10,
      font = helvetica,
      color = BLACK_TEXT,
      maxWidth = CONTENT_WIDTH,
      lineHeight = size * 1.5,
    } = options;

    const safe = sanitizeTextForWinAnsiPdf(text);
    const words = safe.split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let currentLine = "";

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const testWidth = font.widthOfTextAtSize(testLine, size);

      if (testWidth > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) {
      lines.push(currentLine);
    }

    let currentY = startY;
    for (const line of lines) {
      page.drawText(line, { x, y: currentY, size, font, color });
      currentY -= lineHeight;
    }

    return currentY;
  };

  const checkNewPage = (requiredSpace: number) => {
    if (y - requiredSpace < PAGE_MARGIN) {
      page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
      y = PAGE_HEIGHT - PAGE_MARGIN;
    }
  };

  const drawBlock = (label: string, value: string) => {
    checkNewPage(56);
    page.drawText(sanitizeTextForWinAnsiPdf(label.toUpperCase()), {
      x: PAGE_MARGIN,
      y,
      size: 8,
      font: helveticaBold,
      color: GRAY_TEXT,
    });
    y -= 12;
    y = drawText(value, PAGE_MARGIN, y, {
      size: 10,
      font: helvetica,
      color: BLACK_TEXT,
      maxWidth: CONTENT_WIDTH,
    });
    y -= 20;
  };

  checkNewPage(48);
  page.drawRectangle({
    x: PAGE_MARGIN,
    y: y - 28,
    width: CONTENT_WIDTH,
    height: 32,
    color: LIGHT_GRAY,
  });
  page.drawText("SYSTEM VERIFICATION RESULT", {
    x: PAGE_MARGIN + 10,
    y: y - 8,
    size: 14,
    font: helveticaBold,
    color: PRIMARY_COLOR,
  });
  y -= 48;

  drawBlock(
    "GENERATED AT",
    generatedAt.toLocaleString(undefined, { dateStyle: "long", timeStyle: "short" })
  );
  drawBlock("VERIFICATION SOURCE", source != null && source !== "" ? String(source) : "N/A");

  checkNewPage(36);
  page.drawText("VERIFIED COMPANY DETAILS", {
    x: PAGE_MARGIN,
    y,
    size: 11,
    font: helveticaBold,
    color: BLACK_TEXT,
  });
  y -= 22;

  for (const { label, value } of rows) {
    drawBlock(label, value);
  }

  const bytes = await pdfDoc.save();
  const blob = new Blob([bytes as unknown as BlobPart], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const stamp = generatedAt.toISOString().slice(0, 19).replace(/[:T]/g, "-");
  const a = document.createElement("a");
  a.href = url;
  a.download = `system-verification-${stamp}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}
